import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Check, Gift, Sparkles, Shield, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import FundEliteBrandMark from '../../components/branding/FundEliteBrandMark';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Self-Serve Trial Signup Page
 * 
 * This page is for the Self-Serve / Auto Repair customer-facing portal.
 * Domain: app.fundelitehq.ai
 * 
 * Key behaviors:
 * - 30-day free trial with no credit card required (if configured)
 * - Creates user account and self-serve customer profile
 * - Attaches active trial at account creation
 * - Routes user to self-serve onboarding flow
 */
const SelfServeSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setToken, setUser } = useAuth();
  const { resolvedMode } = useTheme();
  const brandTheme = resolvedMode === 'light' ? 'light' : 'dark';
  
  const agencyId = searchParams.get('agency') || 'default_agency';
  const offerId = searchParams.get('offer') || 'auto_repair';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Google login can be disabled via environment variable
  // Set to false to temporarily disable Google login
  const isGoogleLoginEnabled = false; // TEMPORARILY DISABLED - was: process.env.REACT_APP_GOOGLE_LOGIN_ENABLED !== 'false';
  const [loading, setLoading] = useState(false);
  const [offerConfig, setOfferConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Fetch public offer configuration
  useEffect(() => {
    const fetchOfferConfig = async () => {
      try {
        const response = await axios.get(`${API}/selfserve/offers/${agencyId}/public`);
        setOfferConfig(response.data?.auto_repair);
      } catch (err) {
        console.error('Failed to fetch offer config:', err);
        // Use defaults
        setOfferConfig({
          enabled: true,
          trial_available: true,
          trial_length_days: 30,
          no_credit_card_required: true,
          features: [
            'AI-powered credit report analysis',
            'Smart dispute letter generation',
            'Credit builder tools',
            'Educational blueprints',
          ],
        });
      } finally {
        setLoadingConfig(false);
      }
    };
    
    fetchOfferConfig();
  }, [agencyId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/selfserve/signup/trial`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        offer_id: offerId,
        agency_id: agencyId,
      });
      
      const { token, user, trial, onboarding_route } = response.data;
      
      // Set auth state
      localStorage.setItem('fundelite_token', token);
      setToken(token);
      setUser(user);
      
      toast.success(`Welcome! Your ${trial.days_remaining}-day free trial has started.`);
      
      // Use window.location for reliable navigation after signup
      // This ensures the page fully reloads with the new auth state
      const targetRoute = onboarding_route || '/onboarding';
      window.location.href = targetRoute;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/onboarding';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const trialFeatures = offerConfig?.features || [
    'AI-powered credit report analysis',
    'Smart dispute letter generation',
    'Credit builder tools',
    'Educational blueprints',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex" data-testid="selfserve-signup-page">
      {/* Left Side - Branding with Trial Focus */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative">
          <div className="flex items-center gap-3 mb-8" data-testid="fundelite-selfserve-signup-brand">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              theme={brandTheme}
              logoClassName="h-10 w-auto max-w-[200px] object-contain object-left"
            />
          </div>
        </div>

        <div className="relative">
          {/* Trial Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/20 border border-amber-300/30 mb-6">
            <Gift size={18} className="text-amber-300" />
            <span className="text-amber-100 font-semibold">
              {offerConfig?.trial_length_days || 30}-Day Free Trial
            </span>
            {offerConfig?.no_credit_card_required && (
              <span className="text-amber-200/80 text-sm">• No Credit Card Required</span>
            )}
          </div>
          
          <h2 className="text-4xl font-bold text-white leading-tight mb-6">
            Start Repairing Your <br />Credit Today
          </h2>
          <p className="text-lg text-white/80 max-w-md mb-8">
            Get full access to AI-powered credit repair tools. No commitment, no credit card required.
          </p>
          
          <div className="space-y-4">
            {trialFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 text-white/90">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check size={14} />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
          
          {/* Stats */}
          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
              <p className="text-3xl font-bold text-white">85%</p>
              <p className="text-sm text-white/70">Dispute Success Rate</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
              <p className="text-3xl font-bold text-white">+67</p>
              <p className="text-sm text-white/70">Avg Point Increase</p>
            </div>
          </div>
        </div>

        <div className="relative text-white/60 text-sm">
          © 2026 FundElite AI. All rights reserved.
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8" data-testid="fundelite-selfserve-signup-brand-mobile">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              theme={brandTheme}
              logoClassName="h-9 w-auto max-w-[180px] object-contain"
            />
          </div>

          {/* Mobile Trial Badge */}
          <div className="lg:hidden flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200">
              <Gift size={16} className="text-amber-600" />
              <span className="text-amber-700 font-semibold text-sm">
                {offerConfig?.trial_length_days || 30}-Day Free Trial
              </span>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-stone-900">Start Your Free Trial</h2>
            <p className="text-slate-500 mt-2">
              {offerConfig?.no_credit_card_required 
                ? 'No credit card required • Cancel anytime'
                : 'Start your credit repair journey today'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-700">Full Name</Label>
              <IconInput
                id="name"
                name="name"
                type="text"
                icon={User}
                variant="default"
                containerClassName="mt-1.5"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                data-testid="selfserve-signup-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <IconInput
                id="email"
                name="email"
                type="email"
                icon={Mail}
                variant="default"
                containerClassName="mt-1.5"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                data-testid="selfserve-signup-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <IconInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                icon={Lock}
                variant="default"
                containerClassName="mt-1.5"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                data-testid="selfserve-signup-password"
                trailing={(
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 z-[1] flex w-11 items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-[#EAF7F0]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
              <IconInput
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                icon={Lock}
                variant="default"
                containerClassName="mt-1.5"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                data-testid="selfserve-signup-confirm-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 mt-6"
              disabled={loading}
              data-testid="selfserve-signup-submit"
            >
              {loading ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Creating Account...
                </>
              ) : (
                <>
                  Start Free Trial
                  <ArrowRight size={18} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          {isGoogleLoginEnabled && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-slate-50 text-slate-500">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full py-3 border-slate-200 hover:bg-slate-50"
                onClick={handleGoogleSignup}
                data-testid="selfserve-google-signup-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          {/* Trust Indicators */}
          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <Shield size={14} className="text-emerald-500" />
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-1">
              <Zap size={14} className="text-amber-500" />
              <span>Instant Access</span>
            </div>
          </div>

          <p className="text-center mt-6 text-slate-500 text-sm">
            By creating an account, you agree to our{' '}
            <a href="#" className="text-emerald-600 hover:text-emerald-700">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-emerald-600 hover:text-emerald-700">Privacy Policy</a>
          </p>

          <p className="text-center mt-4 text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelfServeSignup;
